// src/pages/ContactPage.js
import React, { useEffect, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { sendMessage } from "../services/contactMessages.api";
import { apiRequest } from "../services/api";
import { LanguageContext } from "../context/LanguageContext";
import TopBanner from "../components/TopBanner";
import MainHeader from "../components/MainHeader";
import Footer from "../components/Footer";
import { FaWhatsapp, FaEnvelope, FaComments } from "react-icons/fa";

function ContactPage() {
  const navigate = useNavigate();
  const { lang } = useContext(LanguageContext);

  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });

  const [sending, setSending] = useState(false);
const [unreadCount, setUnreadCount] = useState(0);
const [unreadLoading, setUnreadLoading] = useState(true);
const [uiError, setUiError] = useState("");

  const whatsappMessage = lang === "EN" ? "I need help" : "أحتاج مساعدة";

  const whatsappLink = `https://wa.me/96179427867?text=${encodeURIComponent(
  whatsappMessage
)}`;

  const TEXT = {
    EN: {
      mainPage: "Main page",
      account: "Account",
      contact: "Contact",
      title: "CONTACT US",
      loadingChat: "Checking messages...",
      subtitle:
        "Need help? Send us a message and our team will reply as soon as possible.",

      chatTitle: "Messages / Chat",
      chatDesc: "Continue your conversation with our support team.",
      openChat: "OPEN CHAT",

      formTitle: "Send us a message",
      fullName: "Full name *",
      phone: "Phone *",
      email: "Email (optional)",
      subject: "Subject",
      message: "Your message *",
      send: "SEND MESSAGE",
      sending: "SENDING...",

      whatsappTitle: "Prefer WhatsApp?",
      whatsappDesc: "Contact us directly on WhatsApp for faster support.",
      whatsappButton: "CONTACT VIA WHATSAPP",

      required: "Please fill your full name, phone number, and message.",
    },
    AR: {
      mainPage: "الصفحة الرئيسية",
      contact: "تواصل معنا",
      account: "الحساب",
      title: "تواصل معنا",
      loadingChat: "جاري التحقق من الرسائل...",
      subtitle:
        "تحتاج إلى مساعدة؟ أرسل لنا رسالة وسيقوم فريقنا بالرد عليك في أقرب وقت.",

      chatTitle: "الرسائل / المحادثة",
      chatDesc: "تابع محادثتك مع فريق الدعم.",
      openChat: "فتح المحادثة",

      formTitle: "أرسل لنا رسالة",
      fullName: "الاسم الكامل *",
      phone: "رقم الهاتف *",
      email: "البريد الإلكتروني (اختياري)",
      subject: "الموضوع",
      message: "رسالتك *",
      send: "إرسال الرسالة",
      sending: "جاري الإرسال...",

      whatsappTitle: "تفضل واتساب؟",
      whatsappDesc: "تواصل معنا مباشرة عبر واتساب للحصول على دعم أسرع.",
      whatsappButton: "تواصل عبر واتساب",

      required: "يرجى تعبئة الاسم الكامل ورقم الهاتف والرسالة.",
    },
  }[lang];

  /* 🔔 unread replies */
  useEffect(() => {
  let mounted = true;

  async function loadUnread() {
    try {
      setUnreadLoading(true);

      if (!localStorage.getItem("esill-token")) {
        if (mounted) setUnreadCount(0);
        return;
      }

      const res = await apiRequest("/contact-messages/unread-count");

      if (mounted) {
        setUnreadCount(Number(res?.unread || 0));
      }
    } catch {
      if (mounted) setUnreadCount(0);
    } finally {
      if (mounted) setUnreadLoading(false);
    }
  }

  loadUnread();

  return () => {
    mounted = false;
  };
}, []);

  /* 👤 Prefill if logged in */
  useEffect(() => {
    try {
      const cachedUser = localStorage.getItem("esill-user");
      if (!cachedUser) return;

      const user = JSON.parse(cachedUser);

      setForm((prev) => ({
        ...prev,
        full_name: user.full_name || "",
        phone: user.phone || "",
        email: user.email || "",
      }));
    } catch {}
  }, []);

  const submit = async (e) => {
  e.preventDefault();

  if (sending) return;

  setUiError("");

    if (!form.full_name.trim() || !form.phone.trim() || !form.message.trim()) {
      setUiError(TEXT.required);
      return;
    }

    try {
      setSending(true);

      const res = await sendMessage(form);

      navigate("/chat", {
        state: { messageId: res.id },
      });
    } catch (err) {
      setUiError(err.message);
    } finally {
      setSending(false);
    }
  };

  const inputClass = `
  w-full
  h-11
  border border-gray-400
  bg-white
  px-3
  text-sm
  outline-none
  focus:border-black
  transition
  disabled:bg-gray-50
  disabled:text-gray-500
  disabled:cursor-wait
`;

  const textareaClass = `
  w-full
  min-h-[130px]
  border border-gray-400
  bg-white
  px-3 py-3
  text-sm
  outline-none
  resize-y
  focus:border-black
  transition
  disabled:bg-gray-50
  disabled:text-gray-500
  disabled:cursor-wait
`;

  const labelClass = "block text-[12px] font-medium text-gray-900 mb-1";

  const outlineButtonClass = `
  w-full
  h-11
  rounded-md
  border-2 border-black
  bg-white
  text-black
  text-sm font-semibold tracking-wide
  hover:bg-black hover:text-white
  active:scale-[0.99]
  transition
  disabled:opacity-60
  disabled:cursor-wait
  disabled:hover:bg-white
  disabled:hover:text-black
`;

  const InfoCard = ({ icon, title, desc, children }) => (
    <div className="border border-gray-200 bg-[#F6EFE9] rounded-xl p-5 shadow-[0_10px_24px_rgba(0,0,0,0.04)]">
      <div className="flex items-start gap-3">
        <span className="w-10 h-10 border border-gray-300 rounded-md flex items-center justify-center shrink-0 text-black">
          {icon}
        </span>

        <div className="flex-1">
          <h3 className="text-[16px] font-bold text-black mb-1">
            {title}
          </h3>

          <p className="text-sm text-gray-600 leading-6">
            {desc}
          </p>
        </div>
      </div>

      {children && <div className="mt-5">{children}</div>}
    </div>
  );

  return (
    <div
      className="w-full min-h-screen bg-[#F6EFE9] overflow-x-hidden"
      dir={lang === "AR" ? "rtl" : "ltr"}
    >
      <TopBanner />
      <MainHeader />

      <main className="bg-[#F6EFE9] px-4 md:px-8 md:pt-[175px]">
        {/* BREADCRUMB */}
<div className="text-[12px] md:text-sm text-gray-900 mb-6">
  <button
    type="button"
    onClick={() => navigate("/")}
    className="hover:text-black hover:underline"
  >
    {TEXT.mainPage}
  </button>

  <span className="mx-2">›</span>

  <button
    type="button"
    onClick={() => navigate("/account")}
    className="hover:text-black hover:underline"
  >
    {TEXT.account}
  </button>

  <span className="mx-2">›</span>

  <span className="text-gray-900">{TEXT.contact}</span>
</div>

        {/* TITLE */}
        <div className={`${lang === "AR" ? "text-right" : "text-left"} mb-2`}>
          <h1 className="text-[16px] md:text-[20px] font-normal tracking-wide text-gray-900 uppercase">
            {TEXT.title}
          </h1>
        </div>

        {/* LINE */}
        <div className="h-[1px] bg-gray-300 mx-1 md:mx-4 mb-8" />

        {/* CONTENT */}
        <div className="max-w-6xl mx-auto pb-12">
          <div className="mb-8">
            <h2 className="hidden text-[30px] md:text-[38px] font-black text-black leading-tight mb-3">
              {TEXT.title}
            </h2>

            <p className="text-sm md:text-base text-gray-600 max-w-2xl leading-7">
              {TEXT.subtitle}
            </p>
          </div>

          {uiError && (
  <div
    className="
      mb-6
      border border-red-200
      bg-red-50
      text-red-700
      text-sm
      px-4 py-3
      rounded-md
      flex items-start gap-2
    "
  >
    <span className="font-bold leading-5">!</span>
    <span>{uiError}</span>
  </div>
)}

          <div className="grid md:grid-cols-[1fr_4px_1fr] gap-8 md:gap-10">
            {/* LEFT SIDE */}
            <section className="space-y-5">
              <InfoCard
                icon={<FaComments className="text-[17px]" />}
                title={TEXT.chatTitle}
                desc={TEXT.chatDesc}
              >
                <button
  type="button"
  disabled={unreadLoading}
  onClick={() => {
    if (!localStorage.getItem("esill-token")) {
      localStorage.setItem("esill-open-login", "1");
      navigate("/create-account");
      return;
    }

    navigate("/chat");
  }}
  className={`${outlineButtonClass} relative`}
>
  {unreadLoading ? TEXT.loadingChat : TEXT.openChat}

  {!unreadLoading && unreadCount > 0 && (
                    <span className="absolute -top-2 -right-2 min-w-[22px] h-[22px] rounded-full bg-red-600 text-white text-xs flex items-center justify-center">
                      {unreadCount}
                    </span>
                  )}
                </button>
              </InfoCard>

              <InfoCard
                icon={<FaWhatsapp className="text-[18px]" />}
                title={TEXT.whatsappTitle}
                desc={TEXT.whatsappDesc}
              >
                <button
                  type="button"
                  onClick={() => window.open(whatsappLink, "_blank")}
                  className={outlineButtonClass}
                >
                  {TEXT.whatsappButton}
                </button>
              </InfoCard>
            </section>

            {/* DESKTOP LINE */}
            <div className="hidden md:block w-[4px] bg-gray-300 min-h-[520px]" />

            {/* RIGHT SIDE FORM */}
            <section>
              <div className="mb-5 flex items-center gap-3">
                <span className="w-10 h-10 border border-gray-300 rounded-md flex items-center justify-center shrink-0">
                  <FaEnvelope className="text-[16px] text-black" />
                </span>

                <h3 className="text-[18px] font-bold text-black">
                  {TEXT.formTitle}
                </h3>
              </div>

              <form onSubmit={submit} className="space-y-5">
                <div>
                  <label className={labelClass}>{TEXT.fullName}</label>
                  <input
                    disabled={sending}
                    value={form.full_name}
                    onChange={(e) =>
                      setForm({ ...form, full_name: e.target.value })
                    }
                    className={inputClass}
                  />
                </div>

                <div>
                  <label className={labelClass}>{TEXT.phone}</label>
                  <input
                    disabled={sending}
                    value={form.phone}
                    onChange={(e) =>
                      setForm({ ...form, phone: e.target.value })
                    }
                    className={inputClass}
                  />
                </div>

                <div>
                  <label className={labelClass}>{TEXT.email}</label>
                  <input
                    disabled={sending}
                    type="email"
                    value={form.email}
                    onChange={(e) =>
                      setForm({ ...form, email: e.target.value })
                    }
                    className={inputClass}
                  />
                </div>

                <div>
                  <label className={labelClass}>{TEXT.subject}</label>
                  <input
                    disabled={sending}
                    value={form.subject}
                    onChange={(e) =>
                      setForm({ ...form, subject: e.target.value })
                    }
                    className={inputClass}
                  />
                </div>

                <div>
                  <label className={labelClass}>{TEXT.message}</label>
                  <textarea
                    disabled={sending}
                    rows={5}
                    value={form.message}
                    onChange={(e) =>
                      setForm({ ...form, message: e.target.value })
                    }
                    className={textareaClass}
                  />
                </div>

                <button
                  type="submit"
                  disabled={sending}
                  className={outlineButtonClass}
                >
                  {sending ? TEXT.sending : TEXT.send}
                </button>
              </form>
            </section>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}

export default ContactPage;